/**
 * Specialized API utilities for guest tools.
 * These tools need to work both authenticated and unauthenticated (guest mode).
 * Uses the configured axios instance to ensure cookies and proxy work correctly.
 */

import api from './api';
import { buildApiUrl } from './apiBase';

/**
 * Guest session management
 */
export const guestSessionApi = {
  /** Get current guest session, auto-starts if needed */
  getSession: async () => {
    try {
      const response = await api.get('/v1/guest/session');
      return response.data;
    } catch (err: any) {
      // If session doesn't exist (404), start a new one
      if (err?.response?.status === 404) {
        await api.post('/v1/guest/start');
        const response = await api.get('/v1/guest/session');
        return response.data;
      }
      throw err;
    }
  },
  
  /** Start a new guest session */
  startSession: async () => {
    const response = await api.post('/v1/guest/start');
    return response.data;
  },
  
  /** Select a repository for guest session */
  selectRepository: async (repositoryId: number) => {
    const response = await api.post('/v1/guest/repository/select', { repositoryId });
    return response.data;
  },
};

/**
 * Branch protection tool API
 */
export const branchProtectionApi = {
  /** Create a snapshot */
  snapshot: async (params: {
    installationId: string;
    organization: string;
    repositories: string[];
    branch?: string;
  }) => {
    const response = await api.post('/v1/tools/branch-protection/snapshot', params);
    return response.data;
  },
  
  /** Generate authenticated diff */
  diff: async (params: {
    installationId: string;
    organization: string;
    repository: string;
    branch?: string;
    fromDate?: string;
    toDate?: string;
    includeAcknowledged?: boolean;
  }) => {
    const response = await api.post('/v1/tools/branch-protection/diff', params);
    return response.data;
  },
  
  /** Generate guest diff (using uploaded baseline file) */
  guestDiff: async (baselineFile: File, organization: string, repository: string) => {
    const formData = new FormData();
    formData.append('baselineSnapshot', baselineFile);
    formData.append('organization', organization);
    formData.append('repository', repository);
    
    const response = await api.post('/v1/guest/tools/branch-protection/diff', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
};

/**
 * PR approvals tool API
 */
export const prApprovalsApi = {
  /** Generate PR approvals report */
  generate: async (params: {
    installationId: string;
    organization: string;
    repositories: string[];
    baseBranch?: string;
    startDate?: string | null;
    endDate?: string | null;
    authorFilter?: string | null;
    requiredApprovalsCount?: number;
  }) => {
    const response = await api.post('/v1/tools/pr-approvals/generate', params);
    return response.data;
  },
};

/**
 * Evidence pack download helpers
 * These return full URLs for browser navigation
 */
export const downloadApi = {
  /** Get download URL for an evidence pack */
  getDownloadUrl: (
    packId: string, 
    format: 'pdf' | 'csv' | 'json' | 'manifest' | 'zip' = 'pdf',
    isGuest: boolean = false
  ): string => {
    const endpoint = isGuest 
      ? `/api/v1/guest/evidence-packs/${packId}/download`
      : `/api/v1/tools/evidence-packs/${packId}/download`;
    return buildApiUrl(`${endpoint}?format=${format}`);
  },
};
